// =============================================================================
// Design tokens -- TickAndPass Approved Brand Manual (docs/BRAND_MANUAL.md)
// Single source of truth for colour, spacing, radii, typography, and shadows.
// =============================================================================

// Approved Primary & Semantic Palette (Section 6)
const primary = "#08833E";
const primaryPressed = "#066A32";
const primaryTint = "#E7F4EC";
const navy = "#16294C";
const navyDark = "#0E1C36";

export const C = {
  // Brand Primary & Navy
  primary,
  primaryDark: primaryPressed,
  primaryLight: primaryTint,
  primaryPressed,
  primaryTint,
  navy,
  navyDark,
  navyCard: "#22395F",
  navyBorder: "#31486E",
  onNavyMuted: "#AAB8D0",

  // Legacy key names kept for backwards compatibility during migration (Section 16)
  blue: primary,
  blueDark: primaryPressed,
  blueLight: primaryTint,

  // Semantic Status Fills & Tints
  success: "#057642",
  successTint: "#DCF5E7",
  warning: "#915907",
  warningTint: "#FFF3D6",
  danger: "#B24020",
  dangerTint: "#FCEBE7",
  dangerPressed: "#9A371B",
  info: navy,
  infoTint: "#E8EDF5",

  // Legacy status aliases
  green: "#057642",
  greenLight: "#DCF5E7",
  amber: "#915907",
  amberLight: "#FFF3D6",
  red: "#B24020",
  redLight: "#FCEBE7",

  // Neutrals -- Navy-tinted Slate Scale
  textStrong: "#182B49", // Body text, names, amounts
  textMuted: "#4E5D78",  // Subtitles, captions, supporting text
  textSubtle: "#8290A9", // Placeholders, disabled text, tertiary icons
  divider: "#E4E8EF",    // Hairline row separators
  border: "#D3DAE6",     // Field borders

  // Legacy neutral aliases
  textDark: "#182B49",
  textMed: "#4E5D78",
  textLight: "#8290A9",
  dividerStrong: "#D3DAE6",

  // Surfaces & Backgrounds
  background: "#F5F7F9",  // Grey canvas behind cards
  surface: "#FFFFFF",     // White cards, sheets, headers, fields
  surfaceSunken: "#EEF2F6", // Inset fills: chips, icon tiles, segmented controls

  // Legacy background aliases
  bg: "#FFFFFF",
  bgMuted: "#F5F7F9",
};

// Approved Font Stack (Section 3) -- Plus Jakarta Sans self-hosted, fallback to Noto Sans Tamil & system
export const font = `"Plus Jakarta Sans", "Noto Sans Tamil", system-ui, sans-serif`;

// Approved Spacing Scale (Section 5, Section 16): 4, 8, 12, 16, 20, 24, 32, 40
export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 32,
  huge: 40,
};

// Approved Radii (Section 5, Section 16): mark 4, control 8, tile 12, card 16, sheet 20, pill 999
export const radii = {
  mark: 4,
  control: 8,
  tile: 12,
  card: 16,
  sheet: 20,
  pill: 999,
  // Compatibility aliases
  sm: 4,
  md: 8,
  lg: 16,
};

// Approved Shadows (Section 5, Section 16)
export const shadows = {
  card: "0 1px 2px rgba(22, 41, 76, 0.06)",
  raised: "0 8px 24px rgba(22, 41, 76, 0.10)",
};
export const shadow = shadows;

// Approved Typography Scale (Section 4 Web column)
export const type = {
  display: {
    fontSize: "32px",
    lineHeight: "40px",
    fontWeight: 700,
    letterSpacing: "-0.2px",
  },
  title: {
    fontSize: "24px",
    lineHeight: "32px",
    fontWeight: 700,
    letterSpacing: "-0.2px",
  },
  heading: {
    fontSize: "18px",
    lineHeight: "26px",
    fontWeight: 600,
    letterSpacing: "0px",
  },
  body: {
    fontSize: "15px",
    lineHeight: "24px",
    fontWeight: 400,
    letterSpacing: "0px",
  },
  bodyStrong: {
    fontSize: "15px",
    lineHeight: "24px",
    fontWeight: 600,
    letterSpacing: "0px",
  },
  label: {
    fontSize: "13px",
    lineHeight: "20px",
    fontWeight: 500,
    letterSpacing: "0px",
  },
  caption: {
    fontSize: "13px",
    lineHeight: "20px",
    fontWeight: 400,
    letterSpacing: "0px",
  },
  overline: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  },
};
